import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Select, Table, message, Card, Space, Typography, Button } from 'antd';
import { UserOutlined, SaveOutlined } from '@ant-design/icons';
import { useLLMOverrides } from './useLLMOverrides';
import { listPractitionersFromOrg } from 'src/store/actions/practitioner.action';
import { predefinedUseCases } from './config';
import { getLlmDisplayText, getLlmDisplayComponent } from 'src/utils/llmUtils';

const { Title, Text } = Typography;

const PractitionerLLMs = ({ selectedOrganization }) => {
  const dispatch = useDispatch();
  const {
    llms,
    llmsLoading,
    llmOverrides,
    llmOverridesLoading,
    initializeTabConfiguration,
    updateTabConfiguration,
    getTabConfiguration,
    getTabSelection,
    setTabSelection,
    getTabUnsavedChanges,
    saveTabConfigurations,
  } = useLLMOverrides();

  // Get practitioners from store
  const { allPractitionerNames, allPractitionerNamesLoading } = useSelector(
    (state) => state.practitionerState
  );

  // Get configurations, selection, and unsaved changes from Redux
  const configurations = getTabConfiguration('practitioner');
  const selectedPractitioner = getTabSelection('practitioner');
  const hasUnsavedChanges = getTabUnsavedChanges('practitioner');

  // Load practitioners when organization changes
  useEffect(() => {
    if (selectedOrganization?.org_uuid) {
      dispatch(listPractitionersFromOrg(selectedOrganization.org_uuid, true));
    }
  }, [dispatch, selectedOrganization?.org_uuid]);

  // Initialize tab configuration when component mounts or when LLM overrides change
  useEffect(() => {
    if (selectedPractitioner) {
      initializeTabConfiguration('practitioner', predefinedUseCases, [
        selectedPractitioner,
      ]);
    }
  }, [
    llmOverrides,
    llms?.llms,
    selectedPractitioner,
    initializeTabConfiguration,
  ]);

  // Also initialize when component becomes visible and has no configuration
  useEffect(() => {
    if (selectedPractitioner) {
      const currentConfig = getTabConfiguration('practitioner');
      const isEmpty = Object.keys(currentConfig).length === 0;
      if (isEmpty) {
        initializeTabConfiguration('practitioner', predefinedUseCases, [
          selectedPractitioner,
        ]);
      }
    }
  }, [selectedPractitioner]);

  // Handle configuration updates
  const handleConfigurationChange = (
    useCaseKey,
    settingKey,
    optionKey,
    field,
    value
  ) => {
    updateTabConfiguration(
      'practitioner',
      useCaseKey,
      settingKey,
      optionKey,
      field,
      value
    );
  };

  // Handle save configurations
  const handleSaveConfigurations = useCallback(async () => {
    if (selectedPractitioner) {
      await saveTabConfigurations('practitioner', predefinedUseCases, [
        selectedPractitioner,
      ]);
    }
  }, [saveTabConfigurations, selectedPractitioner]);

  // Check if all configurations are empty (no models selected)
  const areAllConfigurationsEmpty = () => {
    for (const [useCaseKey, useCase] of Object.entries(predefinedUseCases)) {
      for (const [settingKey, setting] of Object.entries(useCase.settings)) {
        for (const option of setting.options) {
          const optionKey = option.toLowerCase();
          const llmValue =
            configurations[useCaseKey]?.[settingKey]?.[optionKey]?.llm;
          if (llmValue) {
            return false;
          }
        }
      }
    }
    return true;
  };

  // Handle practitioner selection change
  const handlePractitionerSelectionChange = useCallback(
    (value) => {
      setTabSelection('practitioner', value);
      if (value) {
        initializeTabConfiguration('practitioner', predefinedUseCases, [value]);
      }
    },
    [setTabSelection, initializeTabConfiguration]
  );

  // Get LLM options
  const llmOptions =
    llms?.llms?.map((llm, index) => {
      const displayText = getLlmDisplayText(llm);

      return {
        key: llm.llm_id || `llm-${index}`, // Unique key for React
        label: displayText,
        value: llm.model,
        llm_id: llm.llm_id,
        llm: llm, // Store full llm object for rendering
      };
    }) || [];

  // Custom option render to show badges
  const renderOption = (option) => {
    if (!option.llm) return option.label;
    return getLlmDisplayComponent(option.llm);
  };

  // Get practitioner options
  const practitionerOptions = (allPractitionerNames || []).map(
    (practitioner) => ({
      label: `${
        practitioner.practitioner_title
          ? practitioner.practitioner_title + '. '
          : ''
      }${
        practitioner.practitioner_name ||
        practitioner.full_name ||
        `${practitioner.first_name || ''} ${
          practitioner.last_name || ''
        }`.trim()
      }`,
      value: practitioner.practitioner_id,
    })
  );

  // Create table columns for each use case
  const createTableColumns = () => [
    {
      title: 'Verbosity',
      dataIndex: 'value',
      key: 'value',
      width: '50%',
      render: (text) => <span className="text-gray-800 text-sm">{text}</span>,
    },
    {
      title: 'Model',
      dataIndex: 'llm',
      key: 'llm',
      width: '50%',
      render: (_, record) => (
        <Select
          value={
            configurations[record.useCaseKey]?.[record.settingKey]?.[
              record.optionKey
            ]?.llm || null
          }
          onChange={(value) =>
            handleConfigurationChange(
              record.useCaseKey,
              record.settingKey,
              record.optionKey,
              'llm',
              value
            )
          }
          options={llmOptions}
          loading={llmsLoading}
          className="w-full"
          size="small"
          popupMatchSelectWidth={false}
          showSearch
          allowClear
          placeholder="Select a model"
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          disabled={!selectedPractitioner}
          optionRender={renderOption}
        />
      ),
    },
  ];

  // Create table data for a specific use case
  const createTableDataForUseCase = (useCaseKey) => {
    const useCase = predefinedUseCases[useCaseKey];
    const rows = [];

    Object.keys(useCase.settings).forEach((settingKey) => {
      const setting = useCase.settings[settingKey];

      setting.options.forEach((option) => {
        rows.push({
          key: `${useCaseKey}-${settingKey}-${option.toLowerCase()}`,
          useCaseKey,
          settingKey,
          optionKey: option.toLowerCase(),
          value: option,
        });
      });
    });

    return rows;
  };

  // Show organization selection message if no organization is selected
  if (!selectedOrganization) {
    return (
      <div className="text-center py-8">
        <UserOutlined className="text-4xl text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Practitioner Level Configuration
        </h3>
        <p className="text-gray-600">
          Please select an organization first to configure practitioner-level
          LLM overrides.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center gap-2">
          <Text strong>Select Practitioner:</Text>
          <Select
            placeholder="Select a practitioner to configure"
            value={selectedPractitioner}
            onChange={handlePractitionerSelectionChange}
            options={practitionerOptions}
            loading={allPractitionerNamesLoading}
            className="w-64"
            showSearch
            allowClear
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>
      </Card>

      {selectedPractitioner ? (
        <div className="space-y-6">
          {Object.keys(predefinedUseCases).map((useCaseKey) => {
            const useCase = predefinedUseCases[useCaseKey];
            return (
              <Card key={useCaseKey}>
                <div className="mb-4">
                  <Title level={4} className="!mb-1">
                    {useCase.title}
                  </Title>
                  <Text type="secondary" className="text-sm">
                    {useCase.description}
                  </Text>
                </div>
                <Table
                  dataSource={createTableDataForUseCase(useCaseKey)}
                  columns={createTableColumns()}
                  rowKey="key"
                  pagination={false}
                  size="middle"
                  tableLayout="fixed"
                  bordered
                  loading={llmsLoading || llmOverridesLoading}
                  locale={{ emptyText: 'No settings available.' }}
                />
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <div className="text-center py-8">
            <Text type="secondary">
              Select a practitioner above to configure their LLM preferences
            </Text>
          </div>
        </Card>
      )}

      {/* Save button positioned at bottom right */}
      <div className="flex justify-end pt-4">
        <div className="flex items-center gap-2">
          {hasUnsavedChanges && (
            <span className="text-sm text-orange-600">Unsaved changes</span>
          )}
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveConfigurations}
            loading={llmOverridesLoading}
            disabled={
              !hasUnsavedChanges ||
              !selectedOrganization ||
              !selectedPractitioner
            }
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PractitionerLLMs;
